import * as Arr from "effect/Array";
import type { OrchestrationShellSnapshot, OrchestrationShellStreamEvent } from "@amoreg/contracts";

/**
 * Reduce a single shell stream event into an existing snapshot, returning a new
 * snapshot with the event's changes applied. This is a pure reducer that both
 * web and mobile can use to keep their local shell snapshot in sync.
 *
 * Returns the original snapshot reference unchanged if the event is not
 * recognized (forward-compatible).
 */
export function applyShellStreamEvent(
  snapshot: OrchestrationShellSnapshot,
  event: OrchestrationShellStreamEvent,
): OrchestrationShellSnapshot {
  if (event.sequence <= snapshot.snapshotSequence) return snapshot;

  switch (event.kind) {
    case "project-upserted": {
      const projects = snapshot.projects.some((p) => p.id === event.project.id)
        ? Arr.map(snapshot.projects, (p) => (p.id === event.project.id ? event.project : p))
        : Arr.append(snapshot.projects, event.project);
      return { ...snapshot, projects, snapshotSequence: event.sequence };
    }
    case "project-removed":
      return {
        ...snapshot,
        projects: Arr.filter(snapshot.projects, (p) => p.id !== event.projectId),
        snapshotSequence: event.sequence,
      };
    case "thread-upserted": {
      const previous = snapshot.threads.find((thread) => thread.id === event.thread.id);
      // Shell refetches for messages do not carry file invalidations. Preserve
      // the last one so a token cannot disappear and spuriously reload a viewer.
      const revision = event.thread.workspaceRevision ?? previous?.workspaceRevision;
      const thread =
        revision === undefined ? event.thread : { ...event.thread, workspaceRevision: revision };
      const threads = previous
        ? Arr.map(snapshot.threads, (existing) => (existing.id === thread.id ? thread : existing))
        : Arr.append(snapshot.threads, thread);
      return { ...snapshot, threads, snapshotSequence: event.sequence };
    }
    case "thread-removed":
      return {
        ...snapshot,
        threads: Arr.filter(snapshot.threads, (t) => t.id !== event.threadId),
        snapshotSequence: event.sequence,
      };
    default:
      return snapshot;
  }
}

/** A reconnect snapshot invalidates cached files even when no event was replayed.
 * The epoch also covers repeated snapshots at the same stream sequence. */
export function refreshSnapshotWorkspaces(
  snapshot: OrchestrationShellSnapshot,
  epoch: number,
): OrchestrationShellSnapshot {
  return {
    ...snapshot,
    threads: snapshot.threads.map((thread) => ({
      ...thread,
      workspaceRevision: `snapshot:${epoch}:${snapshot.snapshotSequence}`,
    })),
  };
}
